import React from "react";

import { CloseIcon } from "../../lib/icons";
import { buildOrderedLeadSourceOptions, getRootDispositions } from "../../lib/utils";

function LeadFormModal({
  agents,
  availableCities,
  availableRegions,
  dispositionGroups,
  isBtlUser,
  leadForm,
  leadModal,
  leadSources,
  loading,
  loadingAgents,
  onChange,
  onClose,
  onSubmit,
  roleScope,
}) {
  if (!leadModal.open) {
    return null;
  }

  const restrictedManagerEdit =
    leadModal.mode === "edit" && roleScope?.lead_edit_scope === "notes_assignment_only";
  const disableClientFields = restrictedManagerEdit;
  const shouldShowAgentSelect = roleScope?.scope !== "own_leads";
  const phoneNumber = leadForm.phoneNumber || "";
  const whatsappSameAsMobile = leadForm.whatsappSameAsMobile !== false;
  const whatsappNumber = leadForm.whatsappNumber || "";

  const sourceOptions = buildOrderedLeadSourceOptions(leadSources).filter(
    (option) => option.label !== "Other Sources",
  );
  const rootDispositions = getRootDispositions(dispositionGroups || []);
  const selectedRootDisposition = rootDispositions.find(
    (g) => String(g.id) === String(leadForm.dispositionId),
  );
  const availableSubDispositions = selectedRootDisposition?.children || [];
  const selectedSubDisposition = availableSubDispositions.find(
    (item) => String(item.id) === String(leadForm.subDispositionId),
  );
  const followUpDisposition = selectedSubDisposition || selectedRootDisposition;
  const shouldShowSubDisposition = Boolean(leadForm.dispositionId && availableSubDispositions.length > 0);
  const shouldShowNextFollowUp = Boolean(followUpDisposition?.requires_follow_up);

  function handleDispositionChange(value) {
    const nextRoot = rootDispositions.find((item) => String(item.id) === String(value));
    onChange("dispositionId", value);
    onChange("subDispositionId", "");
    if (!nextRoot?.requires_follow_up) {
      onChange("nextFollowUp", "");
    }
  }

  function handleSubDispositionChange(value) {
    const nextSub = availableSubDispositions.find((item) => String(item.id) === String(value));
    onChange("subDispositionId", value);
    if (value && !nextSub?.requires_follow_up) {
      onChange("nextFollowUp", "");
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="modal-kicker">
              {leadModal.mode === "create" ? "Create Lead" : "Edit Lead"}
            </p>
            <h2>{leadModal.mode === "create" ? "Add new lead" : "Update lead details"}</h2>
          </div>
          <button type="button" className="close-button" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <p className="form-required-note" style={{ color: "var(--red)" }}>Fields marked with * are required.</p>

        <form className="form-grid" onSubmit={onSubmit}>

          {/* ── Contact Info ── */}
          <div className="form-section-label full-span">Contact Info</div>

          <label className="field-stack">
            <span>Lead Name <span style={{ color: "var(--red)" }}>*</span></span>
            <input
              value={leadForm.fullName}
              onChange={(event) => onChange("fullName", event.target.value)}
              placeholder="Anjali Kumar"
              disabled={disableClientFields}
              required
            />
          </label>

          <label className="field-stack">
            <span>Mobile <span style={{ color: "var(--red)" }}>*</span></span>
            <input
              type="tel"
              inputMode="numeric"
              value={phoneNumber}
              onChange={(event) => {
                const digits = event.target.value.replace(/\D/g, "").slice(0, 10);
                onChange("phoneNumber", digits);
              }}
              placeholder="10-digit mobile number"
              pattern="[6-9][0-9]{9}"
              minLength={10}
              maxLength={10}
              title="Enter a valid 10-digit Indian mobile number starting with 6–9"
              disabled={disableClientFields}
              required
            />
            {phoneNumber.length > 0 && !/^[6-9]/.test(phoneNumber) && (
              <span style={{ color: "var(--red)", fontSize: 12, marginTop: 3 }}>
                Mobile number must start with 6, 7, 8, or 9
              </span>
            )}
          </label>

          <label className="field-stack">
            <span>Email</span>
            <input
              type="email"
              value={leadForm.email}
              onChange={(event) => onChange("email", event.target.value)}
              placeholder="lead@example.com"
              disabled={disableClientFields}
            />
          </label>

          <div className="field-stack">
            <span className="field-stack-label">WhatsApp</span>
            <div
              className="whatsapp-toggle-row"
              onClick={() => !disableClientFields && onChange("whatsappSameAsMobile", !whatsappSameAsMobile)}
            >
              <button
                type="button"
                className={`whatsapp-toggle-track ${whatsappSameAsMobile ? "is-on" : ""}`}
                disabled={disableClientFields}
                aria-disabled={disableClientFields}
                aria-checked={whatsappSameAsMobile}
                role="switch"
              >
                <span className="whatsapp-toggle-thumb" />
              </button>
              <span className="whatsapp-toggle-label">
                Same as mobile
              </span>
            </div>
            {!whatsappSameAsMobile && (
              <>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={whatsappNumber}
                  onChange={(event) => {
                    const digits = event.target.value.replace(/\D/g, "").slice(0, 10);
                    onChange("whatsappNumber", digits);
                  }}
                  placeholder="WhatsApp number"
                  pattern="[6-9][0-9]{9}"
                  disabled={disableClientFields}
                />
                {whatsappNumber.length > 0 && !/^[6-9]/.test(whatsappNumber) && (
                  <span style={{ color: "var(--red)", fontSize: 12, marginTop: 3 }}>
                    WhatsApp number must start with 6, 7, 8, or 9
                  </span>
                )}
              </>
            )}
          </div>

          {/* ── Lead Classification ── */}
          <div className="form-section-label full-span">Lead Classification</div>

          <label className="field-stack">
            <span>Source <span style={{ color: "var(--red)" }}>*</span></span>
            {isBtlUser ? (
              <div style={{
                padding: "7px 10px",
                background: "var(--bg-secondary, #f5f5f5)",
                border: "1px solid var(--border, #ddd)",
                borderRadius: 6,
                fontSize: 13,
                color: "var(--text-primary)",
                fontWeight: 500,
              }}>
                BTL
              </div>
            ) : (
              <select
                value={leadForm.sourceId}
                onChange={(event) => onChange("sourceId", event.target.value)}
                disabled={disableClientFields}
                required
              >
                <option value="">Select source</option>
                {sourceOptions.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.label}
                  </option>
                ))}
              </select>
            )}
          </label>

          <label className="field-stack">
            <span>Profession <span className="field-optional">Recommended</span></span>
            <select
              value={leadForm.profession}
              onChange={(event) => onChange("profession", event.target.value)}
              disabled={disableClientFields}
            >
              <option value="">Select profession</option>
              <option value="Delivery executive">Delivery executive</option>
              <option value="Business owner">Business owner</option>
              <option value="Student">Student</option>
              <option value="Salaried employee">Salaried employee</option>
              <option value="Others">Others</option>
            </select>
          </label>

          <label className="field-stack">
            <span>Disposition <span className="field-optional">Optional</span></span>
            <select
              value={leadForm.dispositionId}
              onChange={(event) => handleDispositionChange(event.target.value)}
              disabled={disableClientFields}
            >
              <option value="">Select disposition</option>
              {rootDispositions.map((root) => (
                <option key={root.id} value={root.id}>{root.name}</option>
              ))}
            </select>
          </label>

          {shouldShowSubDisposition ? (
            <label className="field-stack">
              <span>Sub-Disposition <span className="field-optional">Optional</span></span>
              <select
                value={leadForm.subDispositionId}
                disabled={disableClientFields}
                onChange={(event) => handleSubDispositionChange(event.target.value)}
              >
                <option value="">Select sub-disposition</option>
                {availableSubDispositions.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </label>
          ) : null}

          {shouldShowNextFollowUp ? (
            <label className="field-stack">
              <span>
                {shouldShowSubDisposition ? "Next Follow-Up" : "Date"}
                {" "}
                <span style={{ color: "var(--red)" }}>*</span>
              </span>
              <input
                type="datetime-local"
                value={leadForm.nextFollowUp || ""}
                onChange={(event) => onChange("nextFollowUp", event.target.value)}
                disabled={disableClientFields}
                required
              />
            </label>
          ) : null}

          {shouldShowAgentSelect ? (
            <label className="field-stack">
              <span>Assigned Agent</span>
              <select
                value={leadForm.assignedToId}
                onChange={(event) => onChange("assignedToId", event.target.value)}
                disabled={loadingAgents}
              >
                <option value="">
                  {loadingAgents ? "Loading visible agents..." : "Select agent"}
                </option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.full_name}
                  </option>
                ))}
              </select>
              <small className="field-helper">
                {loadingAgents
                  ? "Checking which agents are visible for the selected geography."
                  : agents.length
                    ? "Assignment options follow your current geography access."
                    : "No assignable agents are currently visible for this geography."}
              </small>
            </label>
          ) : null}

          {/* ── Geography ── */}
          <div className="form-section-label full-span">Geography</div>

          <label className="field-stack">
            <span>Region <span style={{ color: "var(--red)" }}>*</span></span>
            <select
              value={leadForm.regionName}
              onChange={(event) => {
                onChange("regionName", event.target.value);
                onChange("cityName", "");
              }}
              disabled={disableClientFields}
              required
            >
              <option value="">{disableClientFields ? "No region available" : "Select region"}</option>
              {availableRegions.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </label>

          <label className="field-stack">
            <span>City <span style={{ color: "var(--red)" }}>*</span></span>
            <select
              value={leadForm.cityName}
              onChange={(event) => onChange("cityName", event.target.value)}
              disabled={disableClientFields || !leadForm.regionName}
              required
            >
              <option value="">
                {disableClientFields
                  ? "No city available"
                  : !leadForm.regionName
                    ? "Select a region first"
                    : "Select city"}
              </option>
              {availableCities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
            {leadForm.regionName && (
              <small className="field-helper">
                Showing cities in {leadForm.regionName}.
              </small>
            )}
          </label>

          <label className="field-stack">
            <span>Pincode <span className="field-optional">Optional</span></span>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={leadForm.pincode}
              onChange={(event) => {
                const digits = event.target.value.replace(/\D/g, "").slice(0, 6);
                onChange("pincode", digits);
              }}
              placeholder="6-digit pincode"
              disabled={disableClientFields}
            />
          </label>

          {restrictedManagerEdit ? (
            <p className="helper-text full-span">
              Managers can update only notes and assignment. Client profile fields stay read-only.
            </p>
          ) : null}

          <label className="field-stack full-span">
            <span>Notes <span className="field-optional">Optional</span></span>
            <textarea
              rows="3"
              value={leadForm.notes}
              onChange={(event) => onChange("notes", event.target.value)}
              placeholder="Lead context, requirement, or notes."
            />
          </label>

          <div className="modal-actions full-span">
            <button type="button" className="secondary-ghost" onClick={onClose}>
              Cancel
            </button>
            <button className="primary-button modal-submit" disabled={loading} type="submit">
              {loading ? "Saving..." : leadModal.mode === "create" ? "Create Lead" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default LeadFormModal;
